import {
  calendarDateTimeFromTimestamp,
  DateTime,
  type DocumentCategoryWithoutReadIdAndClientId,
  File as FileModel,
  fromDateTime,
  todayDateTime,
} from '@rentguru/commons-utils';
import { Dexie, Table } from 'dexie';
import isNil from 'lodash/isNil';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { v4 as uuid } from 'uuid';

import { BulkUpdateFolder, FolderDatabaseAction, FolderDatabaseModel, FolderIndexes } from './folderContextUtils';

// Dexie Database Class
class FolderDatabase extends Dexie {
  folders!: Table<FolderDatabaseModel>;
  foldersToSync!: Table<{
    id: string;
    category?: DocumentCategoryWithoutReadIdAndClientId;
    file?: FileModel;
    folderDatabaseAction: FolderDatabaseAction;
    syncTimestamp: number;
  }>;
  metadata!: Table<{ key: string; value: string }>;

  constructor() {
    super('folderDatabase');
    this.version(1).stores({
      folders: 'id, depth, entityId, documentCategoryId, documentId',
      foldersToSync: 'id',
    });
    this.version(2).stores({
      folders: 'id, depth, entityId, documentCategoryId, documentId',
      foldersToSync: 'id',
      metadata: 'key',
    });
  }
}

export const DB_FOLDERS = new FolderDatabase();

const LAST_SYNC_KEY = 'lastSyncedAt';

export const getLastSyncedAt = async (): Promise<DateTime | null> => {
  try {
    const record = await DB_FOLDERS.metadata.get(LAST_SYNC_KEY);
    if (isNil(record)) {
      return null;
    }
    return calendarDateTimeFromTimestamp(record.value);
  } catch {
    return null;
  }
};

export const setLastSyncedAt = async (date: DateTime): Promise<void> => {
  try {
    await DB_FOLDERS.metadata.put({ key: LAST_SYNC_KEY, value: fromDateTime(date) });
  } catch (error) {
    console.error('Error writing lastSyncedAt:', error);
  }
};

export const clearLastSyncedAt = async (): Promise<void> => {
  try {
    await DB_FOLDERS.metadata.delete(LAST_SYNC_KEY);
  } catch (error) {
    console.error('Error clearing lastSyncedAt:', error);
  }
};

export const resetDatabase = async (): Promise<void> => {
  try {
    await DB_FOLDERS.folders.clear();
    await DB_FOLDERS.foldersToSync.clear();
    await DB_FOLDERS.metadata.delete(LAST_SYNC_KEY);
  } catch (error) {
    console.error('Error resetting database:', error);
  }
};

export interface FolderDatabaseContext extends FolderDatabaseState {
  getAllRecords: () => Promise<FolderDatabaseModel[]>;
  addFolderToSync: (
    folderDatabaseAction: FolderDatabaseAction,
    file?: FileModel,
    category?: DocumentCategoryWithoutReadIdAndClientId
  ) => Promise<void>;
  addFolder: (folder: FolderDatabaseModel) => Promise<FolderDatabaseModel | undefined>;
  bulkAddFolders: (folders: FolderDatabaseModel[]) => Promise<FolderDatabaseModel[]>;
  getFolderById: (folderId: string) => Promise<FolderDatabaseModel>;
  getFoldersByIndex: (indexToFilterOn: FolderIndexes, toSearchOn: string) => Promise<FolderDatabaseModel[]>;
  getFoldersByDepthRange: (depthPrefix: string) => Promise<FolderDatabaseModel[]>;
  getFoldersContainingText: (
    searchText: string,
    fieldToSearch?: keyof FolderDatabaseModel
  ) => Promise<FolderDatabaseModel[]>;
  getFoldersWithCustomFilter: (filterFn: (folder: FolderDatabaseModel) => boolean) => Promise<FolderDatabaseModel[]>;
  deleteFolder: (folderId: string) => Promise<void>;
  bulkDeleteFolders: (folderIds: string[]) => Promise<void>;
  deleteFolderByDocumentId: (documentId: string) => Promise<void>;
  updateDatabaseFolder: (folder: FolderDatabaseModel) => Promise<FolderDatabaseModel>;
  bulkUpdateDatabaseFolders: (keysAndChanges: BulkUpdateFolder[]) => Promise<void>;
  deleteItemsFromFoldersToSync: (fileSyncIds: string[]) => Promise<void>;
}

interface FolderDatabaseState {
  isDbConnected: boolean;
  isProcessing: boolean;
  lastFileChange: DateTime;
}

type Action = { type: 'SET_IS_DB_CONNECTED'; payload: boolean } | { type: 'SET_IS_DB_PROCESSING'; payload: boolean };

const initialState: FolderDatabaseState = {
  isDbConnected: false,
  isProcessing: false,
  lastFileChange: todayDateTime(),
};

const folderDatabaseReducer = (state: FolderDatabaseState, action: Action): FolderDatabaseState => {
  switch (action.type) {
    case 'SET_IS_DB_CONNECTED':
      return {
        ...state,
        isDbConnected: action.payload,
      };
    case 'SET_IS_DB_PROCESSING':
      return {
        ...state,
        isProcessing: action.payload,
      };
    default:
      return state;
  }
};

export const FolderDatabaseContext = createContext<FolderDatabaseContext | null>(null);

export const FolderDatabaseContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(folderDatabaseReducer, initialState);

  useEffect(() => {
    const initDB_FOLDERS = async () => {
      try {
        await DB_FOLDERS.open();
        dispatch({ type: 'SET_IS_DB_CONNECTED', payload: true });
      } catch (error) {
        console.error('Database initialization failed:', error);
        dispatch({ type: 'SET_IS_DB_CONNECTED', payload: false });
      }
    };
    initDB_FOLDERS();
  }, []);

  const setIsDbProcessing = (isProcessing: boolean) => {
    dispatch({ type: 'SET_IS_DB_PROCESSING', payload: isProcessing });
  };

  const getAllRecords = async (): Promise<FolderDatabaseModel[]> => {
    try {
      return await DB_FOLDERS.folders.toArray();
    } catch (error) {
      console.error('Error fetching all records:', error);
      return [];
    }
  };

  const addFolderToSync = async (
    folderDatabaseAction: FolderDatabaseAction,
    file?: FileModel,
    category?: DocumentCategoryWithoutReadIdAndClientId
  ): Promise<void> => {
    try {
      await DB_FOLDERS.foldersToSync.add({
        id: uuid(),
        file,
        category,
        folderDatabaseAction,
        syncTimestamp: todayDateTime().epochMilliseconds,
      });
    } catch (error) {
      console.error('Error adding sync item to foldersToSync:', error);
    }
  };

  const deleteItemsFromFoldersToSync = async (fileSyncIds: string[]): Promise<void> => {
    try {
      await DB_FOLDERS.foldersToSync.bulkDelete(fileSyncIds);
    } catch (error) {
      console.error('Error deleting item from folders to sync:', error);
    }
  };

  const addFolder = async (folder: FolderDatabaseModel): Promise<FolderDatabaseModel | undefined> => {
    setIsDbProcessing(true);
    try {
      await DB_FOLDERS.folders.put(folder);
      return folder;
    } catch (error) {
      console.error('Error adding folder:', error);
    } finally {
      setIsDbProcessing(false);
    }
  };

  const bulkAddFolders = async (folders: FolderDatabaseModel[]): Promise<FolderDatabaseModel[]> => {
    setIsDbProcessing(true);
    try {
      await DB_FOLDERS.folders.bulkPut(folders);
      return folders;
    } catch (error) {
      console.error('Error adding folders:', error);
      return [];
    } finally {
      setIsDbProcessing(false);
    }
  };

  const getFolderById = async (folderId: string): Promise<FolderDatabaseModel> => {
    try {
      const folder = await DB_FOLDERS.folders.get(folderId);
      return folder || ({} as FolderDatabaseModel);
    } catch (error) {
      console.error('Error getting folder by ID:', error);
      return {} as FolderDatabaseModel;
    }
  };

  const getFoldersByIndex = async (
    indexToFilterOn: FolderIndexes,
    toSearchOn: string
  ): Promise<FolderDatabaseModel[]> => {
    try {
      return await DB_FOLDERS.folders.where(indexToFilterOn).equals(toSearchOn).toArray();
    } catch (error) {
      console.error('Error getting folders by index:', error);
      return [];
    }
  };

  const getFoldersByDepthRange = async (depthPrefix: string): Promise<FolderDatabaseModel[]> => {
    try {
      return await DB_FOLDERS.folders.where(FolderIndexes.DEPTH).startsWith(depthPrefix).toArray();
    } catch (error) {
      console.error('Error getting folders by depth range:', error);
      return [];
    }
  };

  const getFoldersContainingText = async (
    searchText: string,
    fieldToSearch: keyof FolderDatabaseModel = 'translatedLabel'
  ): Promise<FolderDatabaseModel[]> => {
    try {
      return await DB_FOLDERS.folders
        .filter((folder) => {
          const fieldValue = folder[fieldToSearch];
          if (typeof fieldValue === 'string') {
            return fieldValue.toLowerCase().includes(searchText.toLowerCase());
          }
          return false;
        })
        .toArray();
    } catch (error) {
      console.error('Error searching folders:', error);
      return [];
    }
  };

  const getFoldersWithCustomFilter = async (
    filterFn: (folder: FolderDatabaseModel) => boolean
  ): Promise<FolderDatabaseModel[]> => {
    try {
      return await DB_FOLDERS.folders.filter(filterFn).toArray();
    } catch (error) {
      console.error('Error filtering folders:', error);
      return [];
    }
  };

  const deleteFolder = async (folderId: string): Promise<void> => {
    try {
      await DB_FOLDERS.folders.delete(folderId);
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const bulkDeleteFolders = async (folderIds: string[]): Promise<void> => {
    try {
      await DB_FOLDERS.folders.bulkDelete(folderIds);
    } catch (error) {
      console.error('Error bulk deleting folders:', error);
    }
  };

  const deleteFolderByDocumentId = async (documentId: string): Promise<void> => {
    try {
      const folders = await DB_FOLDERS.folders.where(FolderIndexes.DOCUMENT_ID).equals(documentId).toArray();
      await DB_FOLDERS.folders.bulkDelete(folders.map((folder) => folder.id));
    } catch (error) {
      console.error('Error deleting folder by document ID:', error);
    }
  };

  const updateDatabaseFolder = async (folder: FolderDatabaseModel): Promise<FolderDatabaseModel> => {
    try {
      await DB_FOLDERS.folders.put(folder);
      return folder;
    } catch (error) {
      console.error('Error updating folder:', error);
      return folder;
    }
  };

  const bulkUpdateDatabaseFolders = async (keysAndChanges: BulkUpdateFolder[]): Promise<void> => {
    try {
      await DB_FOLDERS.folders.bulkUpdate(keysAndChanges);
    } catch (error) {
      console.error('Error updating folder:', error);
    }
  };

  const values = useMemo(
    () => ({
      ...state,
      addFolderToSync,
      getAllRecords,
      addFolder,
      bulkAddFolders,
      getFolderById,
      getFoldersByIndex,
      getFoldersByDepthRange,
      getFoldersContainingText,
      getFoldersWithCustomFilter,
      deleteFolder,
      bulkDeleteFolders,
      deleteFolderByDocumentId,
      updateDatabaseFolder,
      bulkUpdateDatabaseFolders,
      deleteItemsFromFoldersToSync,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <FolderDatabaseContext.Provider value={values}>{children}</FolderDatabaseContext.Provider>;
};

export const useFolderDatabase = () => {
  const context = useContext(FolderDatabaseContext);
  if (!context) {
    throw new Error('useFolderDatabase must be used within a FolderDatabaseContextProvider');
  }
  return context;
};
